import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  HbrDivider,
  HbrIcon,
  HbrOption,
  HbrPopover,
  HbrSelect,
} from '@harbor/elements-react-standalone';
import {
  alertsNegativeSmallBorder,
  dotsSix,
  dotsThree,
  refresh,
} from '@harbor/elements/icons';
import './Dashboard.css';
import DataVisualizationComponent from '../../Common/DataVisualizationComponent';
import StbButton from '../../security-toolbox-ui/StbButton';
import { TileDefinition } from '../../../types/dashboard';
import { getScaledStyles } from './DashboardGridHelpers';
import { useUserRegistry } from '../../../hooks/useUserRegistry';

type CurrentTimeFilter =
  | 'all'
  | 'previous_calendar_year'
  | 'current_fiscal_year'
  | 'last_quarter'
  | 'past_1_quarters'
  | 'past_2_quarters'
  | 'past_3_quarters'
  | 'past_4_quarters'
  | 'past_5_quarters'
  | 'past_6_quarters';

const DEFAULT_CURRENT_TIME_FILTER: CurrentTimeFilter = 'all';

const CURRENT_TIME_FILTER_OPTIONS: Array<{ value: CurrentTimeFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'previous_calendar_year', label: 'Previous Calendar Year' },
  { value: 'current_fiscal_year', label: 'Current Fiscal Year' },
  { value: 'last_quarter', label: 'Last Quarter' },
  { value: 'past_1_quarters', label: 'Past 1 Quarter' },
  { value: 'past_2_quarters', label: 'Past 2 Quarters' },
  { value: 'past_3_quarters', label: 'Past 3 Quarters' },
  { value: 'past_4_quarters', label: 'Past 4 Quarters' },
  { value: 'past_5_quarters', label: 'Past 5 Quarters' },
  { value: 'past_6_quarters', label: 'Past 6 Quarters' },
];

function getEffectiveShowLegend(tile: any): boolean {
  if (tile?.show_legend === true) return true;
  if (tile?.show_legend === false) return false;
  return tile?.settings?.show_legend === true || tile?.settings?.showLegend === true;
}

interface DashboardGridCardProps {
  tile: TileDefinition;
  handleUpdateDashboardCard?: (updatedTile: TileDefinition) => void;
  handleRemoveTile?: (tileId: string) => void;
  handleTileError?: (tileId: number, error: string | null) => void;
  width: number;
  settingsOverride?: Record<string, any>;
  selectedDashboardId?: string | null;
}

function getInitialCurrentTimeFilter(tile: TileDefinition): CurrentTimeFilter {
  const persisted = tile?.runtimeContext?.current_time_filter as CurrentTimeFilter | undefined;
  if (persisted) return persisted;

  const defaultDateRange = tile?.settings?.defaultDateRange as CurrentTimeFilter | undefined;
  if (defaultDateRange) return defaultDateRange;

  return DEFAULT_CURRENT_TIME_FILTER;
}

const stopInteractivePropagation = (e: any) => {
  e?.stopPropagation?.();
};

const DashboardGridCard: React.FC<DashboardGridCardProps> = ({
                                                               tile,
                                                               handleUpdateDashboardCard = () => {},
                                                               handleRemoveTile = () => {},
                                                               handleTileError = () => {},
                                                               width,
                                                               settingsOverride = {},
                                                               selectedDashboardId,
                                                             }) => {
  const { userDashboards, updateUserDashboard } = useUserRegistry();

  const contentRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const contentEl = contentRefs.current[String(tile.id)];
  const rect = contentEl?.getBoundingClientRect();
  const availableW = rect?.width ?? 0;
  const availableH = rect?.height ?? 0;
  const calculatedStyles = getScaledStyles(tile, availableW, availableH);

  const [currentTimeFilter, setCurrentTimeFilter] = useState<CurrentTimeFilter>(() =>
    getInitialCurrentTimeFilter(tile)
  );

  useEffect(() => {
    setCurrentTimeFilter(getInitialCurrentTimeFilter(tile));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tile.id, tile?.runtimeContext?.current_time_filter, tile?.settings?.defaultDateRange]);

  const persistCurrentTimeFilter = async (nextValue: CurrentTimeFilter) => {
    if (!selectedDashboardId || !userDashboards?.[selectedDashboardId]) return;

    const dashboard = userDashboards[selectedDashboardId];
    const updatedTiles = (dashboard.tiles || []).map((dashboardTile: any) =>
      Number(dashboardTile.id) === Number(tile.id)
        ? {
          ...dashboardTile,
          runtimeContext: {
            ...(dashboardTile.runtimeContext ?? {}),
            current_time_filter: nextValue,
          },
        }
        : dashboardTile
    );

    await updateUserDashboard({
      ...userDashboards,
      [selectedDashboardId]: {
        ...dashboard,
        tiles: updatedTiles,
      },
    });
  };

  const handleShowLegend = () => {
    handleUpdateDashboardCard({
      ...tile,
      show_legend: true,
      settings: {
        ...(tile.settings ?? {}),
        show_legend: true,
      },
    });
  };

  const handleHideLegend = () => {
    handleUpdateDashboardCard({
      ...tile,
      show_legend: false,
      settings: {
        ...(tile.settings ?? {}),
        show_legend: false,
      },
    });
  };

  const settings = useMemo(() => {
    return Object.keys(settingsOverride).length > 0 ? settingsOverride : tile.settings;
  }, [tile.settings, settingsOverride]);

  const effectiveRuntimeContext = useMemo(() => {
    return {
      ...(tile.runtimeContext ?? {}),
      current_time_filter: currentTimeFilter,
    };
  }, [tile.runtimeContext, currentTimeFilter]);

  const shouldShowTimeFilter = useMemo(() => {
    const effectiveQueries = tile.queries ?? [];
    return effectiveQueries.some((query) => query.enableDateFilter);
  }, [tile.queries]);

  const handleCurrentTimeFilterChange = async (event: any) => {
    const nextValue = (event?.target?.value || DEFAULT_CURRENT_TIME_FILTER) as CurrentTimeFilter;
    setCurrentTimeFilter(nextValue);

    handleUpdateDashboardCard({
      ...tile,
      runtimeContext: {
        ...(tile.runtimeContext ?? {}),
        current_time_filter: nextValue,
      },
    });

    await persistCurrentTimeFilter(nextValue);
  };

  return (
    <div
      key={String(tile.id)}
      className="hbr-css__container-card"
      style={{
        display: 'grid',
        gridTemplateRows: 'min-content minmax(0, 1fr)',
        gridTemplateColumns: '100%',
        gap: 0,
        height: '100%',
        minHeight: 0,
        margin: 0,
        boxSizing: 'border-box',
        border: tile.error ? '2px solid var(--hbr-color-danger)' : '1px solid #f6f6f6',
        position: 'relative',
        padding: '6px 12px',
        width: '100%',
        minWidth: 0,
        overflow: 'hidden',
      }}
      data-item={undefined}
    >
      <div
        slot="header"
        className="tile-header"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          minWidth: 0,
          position: 'relative',
        }}
      >
        <div
          className="tile-title"
          style={{
            fontSize: `${Math.max(16, width / 50)}px`,
            color: tile.error ? 'var(--hbr-color-danger)' : 'inherit',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: '1 1 auto',
          }}
          title={tile.name}
        >
          {tile.name}
        </div>

        <div
          className="tile-actions right-0"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flex: '0 0 auto',
            position: 'relative',
            zIndex: 21,
          }}
        >
          {shouldShowTimeFilter && (
            <div
              className="dashboard-tile-interactive"
              style={{
                width: 220,
                minWidth: 220,
                position: 'relative',
                zIndex: 22,
                pointerEvents: 'auto',
              }}
              onMouseDown={stopInteractivePropagation}
              onClick={stopInteractivePropagation}
              onPointerDown={stopInteractivePropagation}
              onTouchStart={stopInteractivePropagation}
            >
              <HbrSelect
                value={currentTimeFilter}
                onHbr-change={handleCurrentTimeFilterChange}
              >
                {CURRENT_TIME_FILTER_OPTIONS.map((option) => (
                  <HbrOption key={option.value} value={option.value}>
                    {option.label}
                  </HbrOption>
                ))}
              </HbrSelect>
            </div>
          )}

          <div
            className="dashboard-tile-interactive"
            onMouseDown={stopInteractivePropagation}
            onClick={stopInteractivePropagation}
            onPointerDown={stopInteractivePropagation}
            onTouchStart={stopInteractivePropagation}
          >
            <HbrPopover placement="bottom" hoist>
              <StbButton iconOnly variant="text" icon={dotsThree} onClick={() => {}} />
              <div slot="content">
                Tile Actions
                <HbrDivider />
                <ul>
                  {!(
                    tile.chart_type === 'line_chart' ||
                    tile.chart_type === 'metric_chart' ||
                    tile.chart_type === 'datagrid' ||
                    tile.chart_type === 'markdown'
                  ) && (
                    <li>
                      {getEffectiveShowLegend(tile) ? (
                        <StbButton variant="text" onClick={handleHideLegend}>
                          Hide Legend
                        </StbButton>
                      ) : (
                        <StbButton variant="text" onClick={handleShowLegend}>
                          Show Legend
                        </StbButton>
                      )}
                    </li>
                  )}

                  <li>
                    <StbButton variant="text" onClick={() => handleRemoveTile(String(tile.id))}>
                      Remove Tile
                    </StbButton>
                  </li>
                </ul>
              </div>
            </HbrPopover>
          </div>

          <div
            className="dashboard-tile-interactive"
            onMouseDown={stopInteractivePropagation}
            onClick={stopInteractivePropagation}
            onPointerDown={stopInteractivePropagation}
            onTouchStart={stopInteractivePropagation}
          >
            <StbButton
              variant="text"
              iconOnly
              icon={refresh}
              onClick={() => {
                handleUpdateDashboardCard({
                  ...tile,
                });
              }}
            />
          </div>

          {/*
            Drag handle must NOT be inside dashboard-tile-interactive and must NOT
            stop propagation. react-grid-layout listens for mouse/pointer events
            from this handle.
          */}
          <div
            className="drag-handle dashboard-tile-drag-handle"
            title="Drag tile"
            aria-label="Drag tile"
          >
            <StbButton
              variant="text"
              iconOnly
              icon={dotsSix}
              onClick={() => {}}
            />
          </div>
        </div>
      </div>

      <div
        className="min-h-0 relative tile-content"
        ref={(el) => {
          contentRefs.current[String(tile.id)] = el;
        }}
        style={{
          height: '100%',
          minHeight: 0,
          position: 'relative',
          zIndex: 1,
          overflow: 'hidden',
        }}
      >
        {tile.error ? (
          <div
            className="p-3 rounded-md my-4 flex items-start gap-3 text-sm"
            style={{ border: '1px solid var(--hbr-color-danger-35)' }}
          >
            <HbrIcon asset={alertsNegativeSmallBorder} size="20px" sentiment="danger" />
            <div>{tile.error}</div>
          </div>
        ) : (
          (() => {
            const { viewportStyle, canvasStyle } = calculatedStyles;
            return (
              <div
                style={{
                  ...viewportStyle,
                  flex: 1,
                  minHeight: 0,
                  minWidth: 0,
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                <div
                  style={{
                    ...canvasStyle,
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  <DataVisualizationComponent
                    tileDefinition={tile}
                    runtimeContext={effectiveRuntimeContext}
                    settingsOverride={settings}
                    currentTimeFilter={currentTimeFilter}
                    setTileError={(error) => handleTileError(tile.id, error)}
                    refetchTrigger={`dashboard:${tile.id}:${currentTimeFilter}`}
                    displayContext="dashboard"
                  />
                </div>
              </div>
            );
          })()
        )}
      </div>
    </div>
  );
};

export default DashboardGridCard;